import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

/* ============================================================
 * Koebi Label Configurator — 3D Bottle Preview
 * ============================================================
 * Intentionally mirrors the Label Designer's 3D setup in
 * assets/koebi-label-editor-3d.js: same GLB normalization, warm
 * transparent stage, daylight lights, shadows, GTAO and SMAA.
 *
 * Label texture differences are deliberate:
 * - Sorte is fixed to Helles.
 * - The customer can only choose the background color.
 * - The uploaded design is centered in the designer's default design
 *   area and cannot be moved or scaled in this configurator.
 *
 * Maintenance note: if the Label Designer changes its bottle lighting,
 * postprocessing, label dimensions, DESIGN_X/DESIGN_W defaults, or Helles
 * frame assets, update this file too.
 * ============================================================ */

const LABEL_W = 180;
const LABEL_H = 80;
const DESIGN_X = 25;
const DESIGN_W = 130;
const PX_PER_MM = 10;
const DEFAULT_BG_COLOR = '#ffffff';
const IS_MOBILE_QUERY = '(max-width: 899.98px)';
const AUTO_ROTATE_SPEED = 0.0068;
const AUTO_ROTATE_MIN_FRAME_MS = 12;
const MANUAL_DRAG_THRESHOLD_PX = 1.5;
const MOBILE_MOTION_DPR_CAP = 1.75;
const MOBILE_STILL_DPR_CAP = 2;
const PDF_PREVIEW_DPI = 300;
const PDF_PREVIEW_MAX_DIMENSION = 2600;
const PDFJS_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js';
const PDFJS_WORKER_URL = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
const PENDING_LABEL_UPLOAD = {
  dbName: 'koebi-label-upload',
  storeName: 'uploads',
  key: 'pending-hero-upload',
  flag: 'koebiLabelPendingUpload',
  maxBytes: 20 * 1024 * 1024,
};

let pdfJsLoadPromise = null;

const normalizeHex = (value) => {
  if (!value) return null;
  let hex = String(value).trim().toLowerCase();
  if (!hex.startsWith('#')) hex = `#${hex}`;
  if (/^#[0-9a-f]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null;
};

const chooseFrameVariantForHex = (hex) => {
  const normalized = normalizeHex(hex) || DEFAULT_BG_COLOR;
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 128 ? 'Schwarz' : 'Weiss';
};

const setLoadingText = (el, text) => {
  if (el) el.textContent = text;
};

const openPendingLabelUploadDb = () => (
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB nicht verfuegbar'));
      return;
    }

    const request = window.indexedDB.open(PENDING_LABEL_UPLOAD.dbName, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PENDING_LABEL_UPLOAD.storeName)) {
        db.createObjectStore(PENDING_LABEL_UPLOAD.storeName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB konnte nicht geoeffnet werden'));
  })
);

const savePendingLabelUpload = async ({ file, bgColor }) => {
  const db = await openPendingLabelUploadDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(PENDING_LABEL_UPLOAD.storeName, 'readwrite');
      tx.objectStore(PENDING_LABEL_UPLOAD.storeName).put({
        blob: file || null,
        name: file?.name || '',
        type: file?.type || '',
        lastModified: file?.lastModified || Date.now(),
        bgColor: normalizeHex(bgColor) || DEFAULT_BG_COLOR,
        source: 'koebi-label-configurator',
        createdAt: Date.now(),
      }, PENDING_LABEL_UPLOAD.key);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error || new Error('Upload konnte nicht gespeichert werden'));
      tx.onabort = () => reject(tx.error || new Error('Upload-Speicherung abgebrochen'));
    });
  } finally {
    db.close();
  }

  try {
    window.sessionStorage.setItem(PENDING_LABEL_UPLOAD.flag, '1');
  } catch (error) {
    // IndexedDB remains the source of truth.
  }
};

const validateConfiguratorUploadFile = (file) => {
  if (!file) return '';
  if (file.size > PENDING_LABEL_UPLOAD.maxBytes) return 'Datei groesser als 20 MB.';
  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) return '';
  if (mime.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(name)) return '';
  return 'Bitte PDF, PNG, JPG oder WebP hochladen.';
};

const fileExtensionForMime = (mime) => {
  const normalized = (mime || '').toLowerCase();
  if (normalized.includes('jpeg')) return 'jpg';
  if (normalized.includes('png')) return 'png';
  if (normalized.includes('webp')) return 'webp';
  if (normalized.includes('gif')) return 'gif';
  return 'png';
};

const fileNameFromUrl = (url, fallbackName = 'etikett-default') => {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const rawName = pathname.split('/').filter(Boolean).pop() || '';
    const cleanName = decodeURIComponent(rawName).replace(/[^a-z0-9._-]+/gi, '-');
    return cleanName || fallbackName;
  } catch (error) {
    return fallbackName;
  }
};

const fetchImageUrlAsFile = async (url, fallbackName = 'etikett-default') => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Default-Bild konnte nicht geladen werden (${response.status})`);

  const blob = await response.blob();
  const type = blob.type || 'image/png';
  let name = fileNameFromUrl(url, fallbackName);
  if (!/\.[a-z0-9]+$/i.test(name)) name = `${name}.${fileExtensionForMime(type)}`;

  return new File([blob], name, {
    type,
    lastModified: Date.now(),
  });
};

const isPdfFile = (file) => {
  const mime = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return mime === 'application/pdf' || /\.pdf$/i.test(name);
};

const getDrawableWidth = (source) => source?.naturalWidth || source?.videoWidth || source?.width || 1;
const getDrawableHeight = (source) => source?.naturalHeight || source?.videoHeight || source?.height || 1;

const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsLoadPromise) return pdfJsLoadPromise;

  pdfJsLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PDFJS_SCRIPT_URL}"]`);
    const script = existing || document.createElement('script');

    const handleLoad = () => {
      if (window.pdfjsLib) resolve(window.pdfjsLib);
      else reject(new Error('pdf.js konnte nicht initialisiert werden'));
    };
    const handleError = () => reject(new Error('pdf.js konnte nicht geladen werden'));

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existing) {
      script.src = PDFJS_SCRIPT_URL;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return pdfJsLoadPromise;
};

const buildDesignerTargetUrl = (rawHref) => {
  try {
    const url = new URL(rawHref || '/products/dein-etikett-auf-unserem-bier', window.location.origin);
    if (!url.hash) url.hash = 'koebi-label-editor';
    return url.href;
  } catch (error) {
    return '/products/dein-etikett-auf-unserem-bier#koebi-label-editor';
  }
};

class KoebiLabelConfigurator {
  constructor(canvasEl, modelUrl, rootEl = document) {
    this.canvas = canvasEl;
    this.modelUrl = modelUrl;
    this.root = rootEl;
    this.labelMesh = null;
    this.deckelMesh = null;
    this.modelRoot = null;
    this.composer = null;
    this.gtaoPass = null;
    this.designImage = null;
    this.designFile = null;
    this.designImageUrl = null;
    this.designSourceUrl = '';
    const rootData = rootEl.dataset || {};
    this.bgColor = normalizeHex(rootData.defaultBgColor) || DEFAULT_BG_COLOR;
    this.frameUrls = {
      Schwarz: rootData.hellesFrameSchwarzUrl || '',
      Weiss: rootData.hellesFrameWeissUrl || '',
    };
    this.frameImageCache = new Map();
    this.textureVersion = 0;
    this.designLoadVersion = 0;
    this.labelTexture = null;
    this.renderQueued = false;
    this.motionFrame = 0;
    this.isDragging = false;
    this.hasManualRotation = false;
    this.pointerMoved = false;
    this.autoRotateActive = true;
    this.isMotionQuality = false;
    this.motionUsesReducedResolution = false;
    this.isInViewport = true;
    this.lastX = 0;
    this.dragDistanceX = 0;
    this.dragInputType = '';
    this.rotY = -Math.PI * 0.4;
    this.velY = 0;

    this._initRenderer();
    this._initScene();
    this._initCamera();
    this._initLights();
    this._initComposer();
    this._loadModel();
    this._initDrag();
    this._bindEvents();
    this._initVisibilityTracking();
  }

  _initRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.9;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  }

  _initScene() {
    this.scene = new THREE.Scene();
    try {
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      this.scene.environmentIntensity = 0.35;
      pmrem.dispose();
    } catch (error) {
      console.warn('[koebi-label-configurator] PMREM setup skipped:', error);
    }
  }

  _initCamera() {
    this.camera = new THREE.PerspectiveCamera(17, 1, 0.01, 100);
    this.camera.position.set(0, -0.1, 6.1);
    this.camera.lookAt(0, 0, 0);
  }

  _initLights() {
    const day = 0xeef4ff;

    this.scene.add(new THREE.AmbientLight(day, 0.14));

    const key = new THREE.DirectionalLight(day, 4.64);
    key.position.set(5.0, 8.0, 2.5);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = -1.5;
    key.shadow.camera.right = 1.5;
    key.shadow.camera.top = 1.5;
    key.shadow.camera.bottom = -1.5;
    key.shadow.camera.near = 0.1;
    key.shadow.camera.far = 18;
    key.shadow.bias = -0.0001;
    key.shadow.normalBias = 0.004;
    key.shadow.radius = 4;
    if ('intensity' in key.shadow) key.shadow.intensity = 1.0;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(day, 1.12);
    fill.position.set(-3.5, 7.0, 1.5);
    this.scene.add(fill);

    const rim = new THREE.DirectionalLight(day, 2.8);
    rim.position.set(-0.5, 10.0, -3.5);
    this.scene.add(rim);

    const top = new THREE.DirectionalLight(day, 1.76);
    top.position.set(0, 14.0, 0.5);
    this.scene.add(top);
  }

  _initComposer() {
    const isMobile = window.matchMedia(IS_MOBILE_QUERY).matches;
    const composerRT = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      samples: isMobile ? 2 : 8,
    });

    this.composer = new EffectComposer(this.renderer, composerRT);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.gtaoPass = new GTAOPass(this.scene, this.camera, 1, 1);
    if (GTAOPass.OUTPUT && 'Default' in GTAOPass.OUTPUT) {
      this.gtaoPass.output = GTAOPass.OUTPUT.Default;
    }
    if (this.gtaoPass.updateGtaoMaterial) {
      this.gtaoPass.updateGtaoMaterial({
        radius: 0.25,
        distanceExponent: 1.0,
        thickness: 1.0,
        scale: 1.2,
        samples: isMobile ? 4 : 8,
      });
    }
    this.composer.addPass(this.gtaoPass);
    this.composer.addPass(new SMAAPass(1, 1));
    this.composer.addPass(new OutputPass());
    this._setSize();
  }

  _setSize() {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width || parent.clientWidth || 1));
    const height = Math.max(1, Math.round(rect.height || parent.clientHeight || width));
    const isMobile = window.matchMedia(IS_MOBILE_QUERY).matches;
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = this.isMotionQuality && this.motionUsesReducedResolution
      ? (isMobile ? Math.min(rawDpr, MOBILE_MOTION_DPR_CAP) : Math.min(rawDpr, 2))
      : (isMobile ? Math.min(rawDpr, MOBILE_STILL_DPR_CAP) : Math.min(rawDpr * 2, 4));

    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);

    if (this.composer) {
      this.composer.setPixelRatio(dpr);
      this.composer.setSize(width, height);
    }

    if (this.camera) {
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
  }

  _setMotionQuality(enabled, reduceResolution = false) {
    const wasReduced = this.isMotionQuality && this.motionUsesReducedResolution;
    const shouldReduce = Boolean(enabled && reduceResolution);

    this.isMotionQuality = enabled;
    this.motionUsesReducedResolution = shouldReduce;
    if (this.gtaoPass) this.gtaoPass.enabled = !enabled;
    if (wasReduced !== shouldReduce) this._setSize();
  }

  _initDrag() {
    const dragSpeed = window.matchMedia(IS_MOBILE_QUERY).matches ? 0.016 : 0.008;
    const touchDragSpeed = 0.016;

    const startDragAt = (clientX, inputType) => {
      this.isDragging = true;
      this.dragInputType = inputType;
      this.pointerMoved = false;
      this.dragDistanceX = 0;
      this.lastX = clientX;
      this.velY = 0;
      this.canvas.style.cursor = 'grabbing';
      this._startMotionLoop();
    };

    const moveDragTo = (clientX, speed) => {
      if (!this.isDragging) return;
      const deltaX = clientX - this.lastX;
      this.dragDistanceX += Math.abs(deltaX);
      if (this.dragDistanceX >= MANUAL_DRAG_THRESHOLD_PX) {
        this.pointerMoved = true;
        this.hasManualRotation = true;
        this.autoRotateActive = false;
      }
      this.velY = deltaX * speed;
      this.rotY += this.velY;
      this.lastX = clientX;
      if (this.modelRoot) this.modelRoot.rotation.y = this.rotY;
      this._startMotionLoop();
    };

    const endDrag = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.dragInputType = '';
      this.canvas.style.cursor = 'grab';
      if (!this.pointerMoved && this.canAutoRotate()) this._startMotionLoop();
    };

    const getTouchCenterX = (touches) => (
      (touches[0].clientX + touches[1].clientX) / 2
    );

    this.canvas.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      startDragAt(event.clientX, 'pointer');
    });

    window.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch' || this.dragInputType !== 'pointer') return;
      moveDragTo(event.clientX, dragSpeed);
    });

    window.addEventListener('pointerup', (event) => {
      if (event.pointerType === 'touch' || this.dragInputType !== 'pointer') return;
      endDrag();
    });
    window.addEventListener('pointercancel', (event) => {
      if (event.pointerType === 'touch' || this.dragInputType !== 'pointer') return;
      endDrag();
    });

    this.canvas.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 2) return;
      event.preventDefault();
      startDragAt(getTouchCenterX(event.touches), 'touch');
    }, { passive: false });

    window.addEventListener('touchmove', (event) => {
      if (this.dragInputType !== 'touch') return;
      if (event.touches.length !== 2) {
        endDrag();
        return;
      }
      event.preventDefault();
      moveDragTo(getTouchCenterX(event.touches), touchDragSpeed);
    }, { passive: false });

    window.addEventListener('touchend', (event) => {
      if (this.dragInputType !== 'touch') return;
      if (event.touches.length >= 2) return;
      event.preventDefault();
      endDrag();
    }, { passive: false });

    window.addEventListener('touchcancel', (event) => {
      if (this.dragInputType !== 'touch') return;
      event.preventDefault();
      endDrag();
    }, { passive: false });
  }

  _initVisibilityTracking() {
    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.isInViewport = Boolean(entry && entry.isIntersecting);
        if (this.isInViewport && this.canAutoRotate()) {
          this._startMotionLoop();
        }
      }, { threshold: 0.05 });
      this.visibilityObserver.observe(this.root instanceof Element ? this.root : this.canvas);
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.canAutoRotate()) this._startMotionLoop();
    });
  }

  canAutoRotate() {
    return this.autoRotateActive
      && !this.hasManualRotation
      && !this.isDragging
      && this.isInViewport
      && !document.hidden
      && Boolean(this.modelRoot);
  }

  _loadModel() {
    const loadingEl = this.root.querySelector('[data-koebi-label-loading]') || document.getElementById('koebi-bottle-loading');
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);

    loader.load(
      this.modelUrl,
      (gltf) => {
        const box = new THREE.Box3().setFromObject(gltf.scene);
        const size = box.getSize(new THREE.Vector3());
        gltf.scene.scale.setScalar(1.5 / Math.max(size.x, size.y, size.z));

        const scaledBox = new THREE.Box3().setFromObject(gltf.scene);
        const center = scaledBox.getCenter(new THREE.Vector3());
        gltf.scene.position.set(-center.x, -center.y, -center.z);

        const pivot = new THREE.Group();
        pivot.add(gltf.scene);

        gltf.scene.traverse((child) => {
          if (!child.isMesh) return;
          child.castShadow = true;
          child.receiveShadow = true;

          const name = child.name.toLowerCase();
          if (name === 'label') this.labelMesh = child;
          if (name === 'deckel') this.deckelMesh = child;
        });

        if (this.labelMesh) {
          const oldMap = this.labelMesh.material?.map || null;
          this.labelMesh.castShadow = false;
          this.labelMesh.material = new THREE.MeshStandardMaterial({
            map: oldMap,
            transparent: true,
            roughness: 0.85,
            metalness: 0.0,
          });
        }

        if (this.deckelMesh) {
          this.deckelMesh.material = new THREE.MeshStandardMaterial({
            color: new THREE.Color('#000000'),
            roughness: 0.5,
            metalness: 0.65,
          });
        }

        this.modelRoot = pivot;
        this.modelRoot.rotation.y = this.rotY;
        this.scene.add(pivot);
        if (loadingEl) {
          loadingEl.hidden = true;
          loadingEl.style.display = 'none';
        }
        this.applyCurrentLabelTexture();
        this._startMotionLoop();
      },
      (xhr) => {
        if (!loadingEl || !xhr.total) return;
        const pct = Math.min(99, Math.round((xhr.loaded / xhr.total) * 100));
        loadingEl.textContent = `${pct} %`;
      },
      (error) => {
        console.error('[koebi-label-configurator] GLB error:', error);
        setLoadingText(loadingEl, 'Fehler beim Laden des Modells.');
      }
    );
  }

  _bindEvents() {
    window.addEventListener('resize', () => {
      this._setSize();
      this.requestRender();
    });
    window.addEventListener('orientationchange', () => {
      window.setTimeout(() => {
        this._setSize();
        this.requestRender();
      }, 100);
    });
  }

  _renderScene() {
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }

  requestRender() {
    if (this.motionFrame || this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      this._renderScene();
    });
  }

  _startMotionLoop() {
    if (this.motionFrame) return;
    this._setMotionQuality(true, this.canAutoRotate());
    const decay = window.matchMedia(IS_MOBILE_QUERY).matches ? 0.94 : 0.92;
    let lastTime = performance.now();
    let lastRenderTime = 0;

    const step = (time = performance.now()) => {
      this.motionFrame = 0;
      const autoRotating = this.canAutoRotate();
      const frameDelta = Math.min(Math.max(time - lastTime, 1), 50);
      this._setMotionQuality(true, autoRotating);

      if (autoRotating && lastRenderTime && time - lastRenderTime < AUTO_ROTATE_MIN_FRAME_MS) {
        this.motionFrame = requestAnimationFrame(step);
        return;
      }

      lastTime = time;
      lastRenderTime = time;

      if (autoRotating) {
        this.rotY += AUTO_ROTATE_SPEED * (frameDelta / 16.67);
      } else if (!this.isDragging) {
        this.rotY += this.velY;
        this.velY *= decay;
        if (Math.abs(this.velY) < 0.0001) this.velY = 0;
      }
      if (this.modelRoot) this.modelRoot.rotation.y = this.rotY;
      this._renderScene();

      if (this.isDragging || Math.abs(this.velY) >= 0.0001 || this.canAutoRotate()) {
        this.motionFrame = requestAnimationFrame(step);
      } else {
        this._setMotionQuality(false);
        this._renderScene();
      }
    };

    this.motionFrame = requestAnimationFrame(step);
  }

  setBackgroundColor(hex) {
    const normalized = normalizeHex(hex);
    if (!normalized || normalized === this.bgColor) return;
    this.bgColor = normalized;
    this.applyCurrentLabelTexture();
  }

  async applyDesignFile(file) {
    if (!file) return;
    const version = ++this.designLoadVersion;
    try {
      const validationError = validateConfiguratorUploadFile(file);
      if (validationError) throw new Error(validationError);

      const loaded = isPdfFile(file)
        ? await this._loadPdfPreviewFromFile(file)
        : await this._loadImageFromFile(file);
      if (version !== this.designLoadVersion) {
        if (loaded.url) URL.revokeObjectURL(loaded.url);
        return;
      }
      if (this.designImageUrl) URL.revokeObjectURL(this.designImageUrl);
      this.designImage = loaded.image;
      this.designImageUrl = loaded.url || null;
      this.designFile = file;
      this.designSourceUrl = '';
      this._setDesignLoadedState(true, true);
      await this.applyCurrentLabelTexture();
    } catch (error) {
      console.error('[koebi-label-configurator] Design image error:', error);
    }
  }

  async applyDesignUrl(url) {
    const sourceUrl = String(url || '').trim();
    if (!sourceUrl) return;

    const version = ++this.designLoadVersion;
    try {
      const image = await this._loadImageFromUrl(sourceUrl);
      if (version !== this.designLoadVersion) return;

      if (this.designImageUrl) {
        URL.revokeObjectURL(this.designImageUrl);
        this.designImageUrl = null;
      }

      this.designImage = image;
      this.designFile = null;
      this.designSourceUrl = sourceUrl;
      this._setDesignLoadedState(true, false);
      await this.applyCurrentLabelTexture();
    } catch (error) {
      console.error('[koebi-label-configurator] Default design image error:', error);
    }
  }

  _setDesignLoadedState(loaded, markUploadZone = false) {
    if (this.root.classList) {
      this.root.classList.toggle('has-koebi-label-design', loaded);
    }
    const dropzone = this.root.querySelector('[data-koebi-label-dropzone]');
    if (dropzone) dropzone.classList.toggle('has-file', loaded && markUploadZone);
  }

  applyLabelTexture(file) {
    return this.applyDesignFile(file);
  }

  getDesignHandoffFile() {
    if (this.designFile) return Promise.resolve(this.designFile);
    if (!this.designSourceUrl) return Promise.resolve(null);
    return fetchImageUrlAsFile(this.designSourceUrl);
  }

  async applyCurrentLabelTexture() {
    const version = ++this.textureVersion;
    if (!this.labelMesh) return;

    const labelCanvas = await this._buildLabelCanvas();
    if (version !== this.textureVersion) return;

    const texture = this._getLabelTexture(labelCanvas);
    texture.image = labelCanvas;
    texture.needsUpdate = true;

    if (!(this.labelMesh.material instanceof THREE.MeshStandardMaterial)) {
      this.labelMesh.material = new THREE.MeshStandardMaterial({
        transparent: true,
        roughness: 0.85,
        metalness: 0.0,
      });
    }

    if (this.labelMesh.material.map && this.labelMesh.material.map !== texture) {
      this.labelMesh.material.map.dispose();
    }

    this.labelMesh.material.map = texture;
    this.labelMesh.material.transparent = true;
    this.labelMesh.material.roughness = 0.85;
    this.labelMesh.material.metalness = 0.0;
    this.labelMesh.material.needsUpdate = true;
    this.requestRender();
  }

  _getLabelTexture(labelCanvas) {
    if (this.labelTexture) return this.labelTexture;

    const texture = new THREE.CanvasTexture(labelCanvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = false;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.anisotropy = Math.min(4, this.renderer.capabilities.getMaxAnisotropy?.() || 1);
    this.labelTexture = texture;
    return texture;
  }

  async _buildLabelCanvas() {
    const width = LABEL_W * PX_PER_MM;
    const height = LABEL_H * PX_PER_MM;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { colorSpace: 'srgb', willReadFrequently: false });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.fillStyle = this.bgColor || DEFAULT_BG_COLOR;
    ctx.fillRect(0, 0, width, height);

    if (this.designImage) {
      const drawW = DESIGN_W * PX_PER_MM;
      const sourceW = getDrawableWidth(this.designImage);
      const sourceH = getDrawableHeight(this.designImage);
      const drawH = Math.max(1, drawW * (sourceH / sourceW));
      const drawX = (DESIGN_X + DESIGN_W / 2) * PX_PER_MM - drawW / 2;
      const drawY = height / 2 - drawH / 2;
      ctx.save();
      ctx.beginPath();
      ctx.rect(DESIGN_X * PX_PER_MM, 0, DESIGN_W * PX_PER_MM, height);
      ctx.clip();
      ctx.drawImage(this.designImage, Math.round(drawX), Math.round(drawY), Math.round(drawW), Math.round(drawH));
      ctx.restore();
    }

    const frameImage = await this._loadFrameImage(chooseFrameVariantForHex(this.bgColor));
    if (frameImage) {
      ctx.drawImage(frameImage, 0, 0, width, height);
    }

    return canvas;
  }

  _loadFrameImage(variant) {
    const url = this.frameUrls[variant];
    if (!url) return Promise.resolve(null);
    if (this.frameImageCache.has(url)) return this.frameImageCache.get(url);

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = (error) => {
        console.warn('[koebi-label-configurator] Helles frame failed:', error);
        resolve(null);
      };
      image.src = url;
    });
    this.frameImageCache.set(url, promise);
    return promise;
  }

  _loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        resolve({ image, url });
      };
      image.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };
      image.src = url;
    });
  }

  async _loadPdfPreviewFromFile(file) {
    const pdfjsLib = await loadPdfJs();
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
    }

    const bytes = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const targetScale = PDF_PREVIEW_DPI / 72;
    const dimensionCapScale = PDF_PREVIEW_MAX_DIMENSION / Math.max(baseViewport.width, baseViewport.height, 1);
    const scale = Math.max(1, Math.min(targetScale, dimensionCapScale));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d', { alpha: true, colorSpace: 'srgb', willReadFrequently: false });
    await page.render({ canvasContext: ctx, viewport, background: 'transparent' }).promise;
    return { image: canvas, url: null };
  }

  _loadImageFromUrl(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });
  }
}

const initKoebiLabelConfigurators = (scope = document) => {
  let roots = scope.matches && scope.matches('[data-koebi-label-configurator]') ? [scope] : [];
  roots = roots.concat(Array.from(scope.querySelectorAll('[data-koebi-label-configurator]')));
  const legacyCanvas = scope.querySelector('#koebi-bottle-canvas') || document.getElementById('koebi-bottle-canvas');

  if (roots.length === 0 && legacyCanvas) {
    roots = [legacyCanvas.closest('.koebi-konfigurator') || document];
  }

  roots.forEach((root) => {
    const canvas = root.querySelector('[data-koebi-label-canvas]') || root.querySelector('#koebi-bottle-canvas');
    if (!canvas || canvas.dataset.koebiLabelReady === 'true') return;

    canvas.dataset.koebiLabelReady = 'true';
    const cfg = new KoebiLabelConfigurator(canvas, canvas.dataset.modelUrl, root);
    root.koebiLabelConfigurator = cfg;

    const input = root.querySelector('[data-koebi-label-upload]') || root.querySelector('#koebi-label-upload');
    const dropzone = root.querySelector('[data-koebi-label-dropzone]') || root.querySelector('#koebi-dropzone');
    const bgPicker = root.querySelector('[data-koebi-label-bg-picker], [data-koebi-label-color-picker]');
    const bgHex = root.querySelector('[data-koebi-label-bg-hex], [data-koebi-label-hex]');
    const cta = root.querySelector('[data-koebi-label-configurator-cta]');
    const defaultDesignUrl = root.dataset?.defaultDesignUrl || '';

    const updateBgControls = (hex) => {
      const normalized = normalizeHex(hex) || DEFAULT_BG_COLOR;
      if (bgPicker) {
        bgPicker.value = normalized;
        bgPicker.style.background = normalized;
      }
      if (bgHex) bgHex.value = normalized.replace('#', '').toUpperCase();
      cfg.setBackgroundColor(normalized);
    };

    updateBgControls((root.dataset && root.dataset.defaultBgColor) || DEFAULT_BG_COLOR);
    if (defaultDesignUrl) cfg.applyDesignUrl(defaultDesignUrl);

    if (bgPicker) {
      bgPicker.addEventListener('input', (event) => {
        updateBgControls(event.target.value);
      });
    }

    if (bgHex) {
      bgHex.addEventListener('input', (event) => {
        const raw = String(event.target.value || '').replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
        event.target.value = raw.toUpperCase();
        const normalized = normalizeHex(raw);
        if (normalized) updateBgControls(normalized);
      });
    }

    if (input) {
      input.addEventListener('change', (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) cfg.applyDesignFile(file);
      });
    }

    if (cta) {
      cta.addEventListener('click', async (event) => {
        const targetUrl = buildDesignerTargetUrl(cta.getAttribute('href'));
        const file = (input?.files && input.files[0]) || cfg.designFile || null;
        const validationError = validateConfiguratorUploadFile(file);
        if (validationError) {
          console.warn('[koebi-label-configurator] Handoff skipped:', validationError);
          return;
        }

        event.preventDefault();
        cta.classList.add('is-uploading');
        try {
          let handoffFile = file;
          if (!handoffFile) {
            try {
              handoffFile = await cfg.getDesignHandoffFile();
            } catch (error) {
              console.warn('[koebi-label-configurator] Default-Bild konnte nicht uebergeben werden:', error);
            }
          }
          const handoffValidationError = validateConfiguratorUploadFile(handoffFile);
          if (handoffValidationError) {
            console.warn('[koebi-label-configurator] Handoff skipped:', handoffValidationError);
            return;
          }
          await savePendingLabelUpload({ file: handoffFile, bgColor: cfg.bgColor });
        } catch (error) {
          console.warn('[koebi-label-configurator] Handoff konnte nicht gespeichert werden:', error);
        } finally {
          window.location.href = targetUrl;
        }
      });
    }

    if (dropzone && input) {
      dropzone.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          input.click();
        }
      });

      ['dragenter', 'dragover'].forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          dropzone.classList.add('is-dragover');
        });
      });

      ['dragleave', 'dragend', 'drop'].forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          dropzone.classList.remove('is-dragover');
        });
      });

      dropzone.addEventListener('drop', (event) => {
        const file = event.dataTransfer && event.dataTransfer.files[0];
        if (!file) return;
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        input.dispatchEvent(new Event('change'));
      });
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initKoebiLabelConfigurators(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initKoebiLabelConfigurators(event.target);
});
